// A select that shows two lines per option and filters as you type.
//
// Built for the estimate editor's item row, where the three selectors it replaced were chip rows:
// fine for the two suppliers a line usually has, unusable for a manufacturer's full product list,
// which wraps to four rows of near-identical names ("GAF Seal-A-Ridge hip and ridge cap" beside
// "GAF TimberTex Premium Ridge Cap hip and ridge cap") and has to be read in full to be told apart.
//
// TWO LINES per option, because one is not enough to tell catalogue items apart: the name is the
// marketing name and the thing that actually distinguishes two of them — the item code, the tier,
// the price a supplier quotes — is the second line.
//
// The search box only appears once the list is long enough to need it (`searchThreshold`), so a
// two-supplier line does not grow a text input it will never use.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface SearchableSelectOption {
  value: string;
  /** First line — the name. */
  label: string;
  /** Second line — what actually tells two similar options apart. */
  description?: string | null;
  /** Right-aligned on the first line (a price, a coverage count). */
  meta?: string | null;
  disabled?: boolean;
  /** Shown in place of `meta` when disabled, to say why. */
  disabledReason?: string | null;
}

interface Props {
  value: string | null;
  options: SearchableSelectOption[];
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** Re-selecting the current value clears it (null). The supplier row relies on this to hand a
   *  line back to automatic resolution; the product row does not, since a line must have an item. */
  allowClear?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  /** Show the filter box from this many options onward. */
  searchThreshold?: number;
  className?: string;
}

export default function SearchableSelect({
  value, options, onChange, placeholder, allowClear = false, disabled = false,
  ariaLabel, searchThreshold = 6, className = '',
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;
  const showSearch = options.length >= searchThreshold;

  // Pointer-down rather than click: a click that starts inside and ends outside (a drag across the
  // list) would otherwise close the panel on mouse-up.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener('pointerdown', onPointerDown);
    // Capture phase, so Esc closes THIS panel without also reaching the modal behind it and
    // closing the whole editor.
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  useEffect(() => {
    if (open && showSearch) searchRef.current?.focus();
    if (!open) setQuery('');
  }, [open, showSearch]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => `${o.label} ${o.description ?? ''} ${o.value}`.toLowerCase().includes(needle));
  }, [options, query]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-ui-border bg-ui-card px-2.5 py-1.5 text-left hover:bg-ui-hover disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-semibold text-ui-text">
            {selected?.label ?? placeholder ?? t('common.select', 'Select…')}
          </span>
          {selected?.description && (
            <span className="mt-0.5 block truncate text-xs text-ui-text3">{selected.description}</span>
          )}
        </span>
        {selected?.meta && (
          <span className="flex-shrink-0 text-xs text-ui-text2">{selected.meta}</span>
        )}
        <span className="flex-shrink-0 text-xs text-ui-text3">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-lg border border-ui-border bg-ui-card shadow-lg">
          {showSearch && (
            <div className="border-b border-ui-border p-1.5">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('common.search', 'Search…')}
                className="w-full rounded-md border border-ui-border bg-ui-card px-2 py-1 text-xs text-ui-text placeholder:text-ui-text3 focus:border-ui-ring focus:outline-none"
              />
            </div>
          )}
          <div role="listbox" className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="px-2.5 py-2 text-xs text-ui-text3">
                {t('common.noMatches', 'No matches.')}
              </p>
            )}
            {filtered.map((o) => {
              const active = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  disabled={o.disabled}
                  title={o.disabled ? o.disabledReason ?? undefined : undefined}
                  onClick={() => {
                    setOpen(false);
                    if (active) { if (allowClear) onChange(null); return; }
                    onChange(o.value);
                  }}
                  className={`flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left ${
                    o.disabled
                      ? 'cursor-not-allowed opacity-50'
                      : active ? 'bg-accent-soft' : 'hover:bg-ui-hover'
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-xs ${active ? 'font-semibold text-accent-text' : 'text-ui-text'}`}>
                      {o.label}
                    </span>
                    {o.description && (
                      <span className="mt-0.5 block truncate text-xs text-ui-text3">{o.description}</span>
                    )}
                  </span>
                  {(o.disabled ? o.disabledReason : o.meta) && (
                    <span className="flex-shrink-0 text-xs text-ui-text2">
                      {o.disabled ? o.disabledReason : o.meta}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
